import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Button, Card } from "../components/ui";
import { ApiUnavailableState, EmptyState, LoadingState, NotConfiguredState } from "../components/states";
import { PlanCard, PlanChangeImpacts } from "../components/billing";
import { useAppContext } from "../hooks/useAppContext";
import {
  api,
  apiConfigured,
  ApiError,
  type DowngradeAssessment,
  type PlanChangeDirection,
  type PublicPricing,
  type SafeSubscriptionView,
} from "../lib/api";

/**
 * Plan comparison (Phase 9, section 38/39).
 *
 * Shows the current plan alongside available plans: features, usage limits, period,
 * price, trial, and — when a target is chosen — the effective timing and
 * configuration impact from the server's plan-change-review (a non-destructive
 * `assessDowngrade`). Nothing is deleted or applied here; this is review only.
 *
 * HUMANE DESIGN: the FREE plan is always shown, no plan is auto-highlighted by
 * price, there is no "Most Popular" badge, and no urgency or scarcity copy.
 */

export function PlanComparisonView({
  pricing,
  currentPlanCode,
  onReview,
  reviewingTarget,
  assessment,
  reviewError,
}: {
  pricing: PublicPricing;
  currentPlanCode: string;
  onReview: (planCode: string) => void;
  reviewingTarget: string | null;
  assessment: DowngradeAssessment | null;
  reviewError: string | null;
}): JSX.Element {
  return (
    <div style={{ display: "grid", gap: "1rem" }}>
      <div className="card-grid">
        {pricing.plans.map((plan) => {
          const isCurrent = plan.planCode === currentPlanCode;
          return (
            <PlanCard
              key={plan.planCode}
              plan={plan}
              current={isCurrent}
              action={
                isCurrent ? (
                  <span className="muted">This is your current plan.</span>
                ) : (
                  <Button
                    variant="secondary"
                    onClick={() => onReview(plan.planCode)}
                    disabled={reviewingTarget === plan.planCode}
                  >
                    {reviewingTarget === plan.planCode ? "Reviewing…" : "Review change"}
                  </Button>
                )
              }
            />
          );
        })}
      </div>

      {reviewError ? (
        <div className="banner-item banner-item--warn" role="status">
          <div className="banner-item__message">{reviewError}</div>
        </div>
      ) : null}

      {assessment ? (
        <Card title={`Changing from ${assessment.fromPlan} to ${assessment.toPlan}`}>
          <p className="muted">{PLAN_CHANGE_TIMING[assessment.direction]}</p>
          <PlanChangeImpacts assessment={assessment} />
          <p className="muted">{pricing.taxNote}</p>
          <div className="row" style={{ gap: "0.75rem", marginTop: "0.5rem" }}>
            <Link to={`/app/billing/checkout?plan=${encodeURIComponent(assessment.toPlan)}`}>
              <Button variant="primary">Continue</Button>
            </Link>
          </div>
        </Card>
      ) : null}
    </div>
  );
}

/**
 * Timing copy per direction. The direction comes from the server, computed on the
 * canonical plan ladder — it is NOT inferred here from the order of the public
 * pricing list. That old heuristic (`findIndex` over `pricing.plans`) reported
 * "downgrade" for any plan the public list happened to omit, because both ranks
 * resolved to -1 and -1 > -1 is false.
 */
const PLAN_CHANGE_TIMING: Readonly<Record<PlanChangeDirection, string>> = {
  upgrade:
    "This is an upgrade. New allowances and features apply as soon as the change is confirmed through the hosted checkout or customer portal.",
  downgrade:
    "This is a downgrade. Your current plan stays active until the end of the paid period; the new plan applies after that. Nothing in Discord is deleted.",
  lateral:
    "Your current plan stays active until the end of the paid period; the new plan applies after that. Nothing in Discord is deleted.",
};

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; pricing: PublicPricing; subscription: SafeSubscriptionView }
  | { kind: "unavailable"; detail?: string };

export function PlanComparison(): JSX.Element {
  const { tenantId, tenantName } = useAppContext();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [reviewingTarget, setReviewingTarget] = useState<string | null>(null);
  const [assessment, setAssessment] = useState<DowngradeAssessment | null>(null);
  const [reviewError, setReviewError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!apiConfigured || !tenantId) return;
    setState({ kind: "loading" });
    Promise.all([api.billing.plans(), api.billing.subscription(tenantId)])
      .then(([pricing, sub]) => setState({ kind: "ready", pricing, subscription: sub.subscription }))
      .catch((error: unknown) =>
        setState({ kind: "unavailable", detail: error instanceof Error ? error.message : undefined }),
      );
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const onReview = useCallback(
    async (planCode: string) => {
      if (!tenantId) return;
      setReviewingTarget(planCode);
      setReviewError(null);
      setAssessment(null);
      try {
        const res = await api.billing.planChangeReview(tenantId, planCode);
        setAssessment(res.assessment);
      } catch (error) {
        setReviewError(error instanceof ApiError ? error.message : "Could not review this change. Please retry.");
      } finally {
        setReviewingTarget(null);
      }
    },
    [tenantId],
  );

  if (!apiConfigured) {
    return (
      <>
        <PageHeader title="Compare plans" />
        <NotConfiguredState title="Control API URL not configured">
          Set <code>PUBLIC_API_URL</code> when building the dashboard to compare plans.
        </NotConfiguredState>
      </>
    );
  }

  if (!tenantId) {
    return (
      <>
        <PageHeader title="Compare plans" />
        <EmptyState title="Select a workspace">
          Use the workspace menu at the top left to choose a workspace — or create your first one — to
          compare plans and subscribe.
        </EmptyState>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Compare plans">
        <p>Review how each plan compares to your current plan for {tenantName ?? "this workspace"}.</p>
      </PageHeader>
      {state.kind === "loading" ? <LoadingState label="Loading plans…" /> : null}
      {state.kind === "unavailable" ? (
        <div className="state" role="alert">
          <ApiUnavailableState detail={state.detail} />
          <Button variant="secondary" onClick={load}>
            Retry
          </Button>
        </div>
      ) : null}
      {state.kind === "ready" ? (
        <PlanComparisonView
          pricing={state.pricing}
          currentPlanCode={state.subscription.planCode}
          onReview={onReview}
          reviewingTarget={reviewingTarget}
          assessment={assessment}
          reviewError={reviewError}
        />
      ) : null}
    </>
  );
}
